import { Button, EmptyState, Field, formatDate, Spinner } from '@freightapi/ui'
import { createFileRoute } from '@tanstack/react-router'
import { type FormEvent, useCallback, useEffect, useState } from 'react'
import { SecretPanel } from '../components/SecretPanel'
import {
  API_KEY_SCOPES,
  type ApiError,
  type ApiKey,
  asApiError,
  type CreatedApiKey,
  createApiKey,
  fetchApiKeys,
  type Mode,
  requestLiveAccess,
  revokeApiKey,
} from '../lib/api'
import { useConsoleI18n } from '../lib/locale'
import { useMode } from '../lib/mode'
import { useCan, useCurrentSession } from '../lib/session'

/**
 * API keys (M3-03). The list is per mode: the switch in the header decides whether this is the
 * sandbox or the real world, and the API answers about that half of the organisation only.
 *
 * Two rules shape this screen. A key is shown once — after the 201 the console knows nothing but
 * the prefix, so the panel with the secret is the only chance anyone gets. And live is not
 * something an organisation grants itself: without `live_enabled` the form cannot create a live
 * key, and the button next to the explanation sends a request to Cargofollow instead.
 */
export const Route = createFileRoute('/_app/api-keys')({ component: ApiKeys })

function ApiKeys() {
  const { t, locale } = useConsoleI18n()
  const can = useCan()
  const session = useCurrentSession()
  const { mode, setMode, liveAvailable } = useMode()
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ready'; keys: readonly ApiKey[] }
    | { status: 'error'; error: ApiError }
  >({ status: 'loading' })
  const [created, setCreated] = useState<CreatedApiKey | undefined>(undefined)
  const [error, setError] = useState<string | undefined>(undefined)
  const [notice, setNotice] = useState<string | undefined>(undefined)
  const [formOpen, setFormOpen] = useState(false)

  const reload = useCallback(async () => {
    try {
      setState({ status: 'ready', keys: (await fetchApiKeys(mode)).keys })
    } catch (cause) {
      setState({ status: 'error', error: asApiError(cause) })
    }
  }, [mode])

  useEffect(() => {
    void reload()
  }, [reload])

  const run = useCallback(
    async (action: () => Promise<unknown>) => {
      setError(undefined)
      try {
        await action()
        await reload()
        return true
      } catch (cause) {
        const failure = asApiError(cause)
        setError(failure.problem?.detail ?? t(failure.messageKey))
        return false
      }
    },
    [reload, t],
  )

  const write = can('keys:write')

  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-semibold text-display text-ink">{t('nav.api_keys.label')}</h1>
        {write ? (
          <Button onClick={() => setFormOpen((open) => !open)}>{t('api_keys.new')}</Button>
        ) : null}
      </div>
      <p className="text-body text-muted">{t('api_keys.intro')}</p>

      {error === undefined ? null : (
        <p role="alert" className="text-body text-danger">
          {error}
        </p>
      )}
      {notice === undefined ? null : (
        <p role="status" className="text-body text-muted">
          {notice}
        </p>
      )}

      {created === undefined ? null : (
        <SecretPanel
          titleKey="api_keys.created.title"
          noteKey="api_keys.created.once"
          secret={created.secret}
          dismissKey="api_keys.created.done"
          onDismiss={() => setCreated(undefined)}
        />
      )}

      {write && formOpen ? (
        <KeyForm
          mode={mode}
          liveAvailable={liveAvailable}
          onCreate={async (input) => {
            setError(undefined)
            try {
              const { key } = await createApiKey(input)
              setCreated(key)
              setFormOpen(false)
              // A live key made from the test screen belongs to a list this screen is not showing,
              // so the console follows the key it just made instead of appearing to lose it.
              if (key.mode === mode) await reload()
              else setMode(key.mode)
              return true
            } catch (cause) {
              const failure = asApiError(cause)
              setError(failure.problem?.detail ?? t(failure.messageKey))
              return false
            }
          }}
          onCancel={() => setFormOpen(false)}
        />
      ) : null}

      {liveAvailable || !can('organization:update') ? null : (
        <section className="flex flex-col gap-3 rounded-card border border-line p-4">
          <h2 className="font-semibold text-lead text-ink">{t('live.title')}</h2>
          <p className="text-body text-muted">{t('api_keys.live.blocked')}</p>
          <div>
            <Button
              variant="ghost"
              onClick={() =>
                void run(async () => {
                  await requestLiveAccess()
                  setNotice(t('live.request.sent', { email: session.user.email }))
                })
              }
            >
              {t('live.request')}
            </Button>
          </div>
        </section>
      )}

      {state.status === 'loading' ? (
        <div className="flex justify-center py-10">
          <Spinner label={t('loading')} size="lg" />
        </div>
      ) : state.status === 'error' ? (
        <EmptyState title={t('error.title')} description={t(state.error.messageKey)} />
      ) : state.keys.length === 0 ? (
        <EmptyState title={t('api_keys.empty')} description={t('api_keys.intro')} />
      ) : (
        <table className="w-full border-collapse text-left text-body">
          <thead>
            <tr className="border-line border-b text-caption text-muted">
              <th scope="col" className="py-2 pr-4 font-medium">
                {t('api_keys.column.name')}
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                {t('api_keys.column.prefix')}
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                {t('api_keys.column.scopes')}
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                {t('api_keys.column.created_by')}
              </th>
              <th scope="col" className="py-2 pr-4 font-medium">
                {t('api_keys.column.last_used')}
              </th>
              <th scope="col" className="py-2 font-medium">
                {t('api_keys.column.actions')}
              </th>
            </tr>
          </thead>
          <tbody>
            {state.keys.map((key) => (
              <tr key={key.id} className="border-line border-b align-top">
                <td className="py-3 pr-4">
                  <span className="block font-medium text-ink">{key.name}</span>
                  <span className="block text-caption text-muted">
                    {formatDate(key.created_at, locale)}
                    {key.revoked_at === null ? '' : ` · ${t('api_keys.status.revoked')}`}
                  </span>
                </td>
                <td className="py-3 pr-4 text-caption text-muted">
                  <span className="block font-mono">{key.prefix}…</span>
                  <span className="block">{t(`mode.${key.mode}`)}</span>
                </td>
                <td className="py-3 pr-4 text-caption text-muted">{key.scopes.join(', ')}</td>
                <td className="py-3 pr-4 text-caption text-muted">
                  {key.created_by ?? t('api_keys.unknown_creator')}
                </td>
                <td className="py-3 pr-4 text-caption text-muted">
                  {key.last_used_at === null
                    ? t('api_keys.never_used')
                    : formatDate(key.last_used_at, locale)}
                </td>
                <td className="py-3">
                  {write && key.revoked_at === null ? (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        if (!window.confirm(t('api_keys.revoke.confirm', { name: key.name })))
                          return
                        void run(() => revokeApiKey(key.id, mode))
                      }}
                    >
                      {t('api_keys.revoke')}
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
}

function KeyForm({
  mode,
  liveAvailable,
  onCreate,
  onCancel,
}: {
  readonly mode: Mode
  readonly liveAvailable: boolean
  readonly onCreate: (input: {
    name: string
    mode: Mode
    scopes: readonly string[]
  }) => Promise<boolean>
  readonly onCancel: () => void
}) {
  const { t } = useConsoleI18n()
  const [name, setName] = useState('')
  const [keyMode, setKeyMode] = useState<Mode>(mode)
  const [scopes, setScopes] = useState<readonly string[]>(API_KEY_SCOPES)
  const [busy, setBusy] = useState(false)

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setBusy(true)
    const created = await onCreate({ name: name.trim(), mode: keyMode, scopes })
    setBusy(false)
    if (created) setName('')
  }

  return (
    <form
      className="flex flex-col gap-4 rounded-card border border-line p-4"
      onSubmit={(event) => void onSubmit(event)}
    >
      <h2 className="font-semibold text-lead text-ink">{t('api_keys.form.title')}</h2>
      <div className="flex flex-col gap-4 md:flex-row md:items-end">
        <Field
          label={t('api_keys.form.name')}
          hint={t('api_keys.form.name.hint')}
          required
          className="grow"
        >
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              type="text"
              required
              maxLength={120}
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="min-h-touch w-full rounded-card border border-line bg-surface px-3 text-body text-ink"
            />
          )}
        </Field>
        <Field
          label={t('api_keys.form.mode')}
          // The reason live cannot be picked belongs next to the control, not in a tooltip.
          {...(liveAvailable ? {} : { hint: t('mode.live.disabled') })}
        >
          {({ id, describedBy }) => (
            <select
              id={id}
              aria-describedby={describedBy}
              value={keyMode}
              onChange={(event) => setKeyMode(event.target.value as Mode)}
              className="min-h-touch rounded-card border border-line bg-surface px-2 text-body text-ink"
            >
              <option value="test">{t('mode.test')}</option>
              {/* Live is not offered at all until Cargofollow enables it; the API refuses it too. */}
              <option value="live" disabled={!liveAvailable}>
                {t('mode.live')}
              </option>
            </select>
          )}
        </Field>
      </div>
      <fieldset className="flex flex-col gap-2 border-0 p-0">
        <legend className="font-medium text-body text-ink">{t('api_keys.form.scopes')}</legend>
        <div className="flex flex-wrap gap-3">
          {API_KEY_SCOPES.map((scope) => (
            <label key={scope} className="flex items-center gap-2 text-body text-muted">
              <input
                type="checkbox"
                checked={scopes.includes(scope)}
                onChange={(event) =>
                  setScopes((current) =>
                    event.target.checked
                      ? [...current, scope]
                      : current.filter((entry) => entry !== scope),
                  )
                }
              />
              <span className="font-mono text-caption">{scope}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <div className="flex flex-wrap gap-2">
        <Button type="submit" busy={busy} busyLabel={t('loading')} disabled={scopes.length === 0}>
          {t('api_keys.form.submit')}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('button.cancel')}
        </Button>
      </div>
    </form>
  )
}
